import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  tasksApi,
  type TaskFilters,
  type CreateTaskInput,
  type UpdateTaskInput,
} from '@/lib/api/tasks';
import { featureKeys } from './use-features';
import { issueKeys } from './use-issues';

export const taskKeys = {
  all: ['tasks'] as const,
  lists: () => [...taskKeys.all, 'list'] as const,
  list: (filters: TaskFilters) => [...taskKeys.lists(), filters] as const,
  details: () => [...taskKeys.all, 'detail'] as const,
  detail: (id: string) => [...taskKeys.details(), id] as const,
  compute: (id: string) => [...taskKeys.detail(id), 'compute'] as const,
};

export function useTasks(filters: TaskFilters = {}) {
  return useInfiniteQuery({
    queryKey: taskKeys.list(filters),
    queryFn: ({ pageParam }) =>
      tasksApi.list({ ...filters, cursor: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.meta?.hasMore && lastPage.meta.cursor ? lastPage.meta.cursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!filters.featureId || !!filters.epicId,
  });
}

export function useTask(id: string) {
  return useQuery({
    queryKey: taskKeys.detail(id),
    queryFn: () => tasksApi.get(id),
    enabled: !!id,
    select: (response) => response.data,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTaskInput) => tasksApi.create(input),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      // Also invalidate the parent feature to update task count
      queryClient.invalidateQueries({
        queryKey: featureKeys.detail(response.data.featureId),
      });
      // Invalidate feature lists and issues list to update counts
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueKeys.all });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateTaskInput) => tasksApi.update(input),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      queryClient.setQueryData(taskKeys.detail(response.data.id), response);
      // Invalidate feature lists and issues list to update completed counts
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueKeys.all });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tasksApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      // Invalidate feature lists and issues list to update counts
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: issueKeys.all });
    },
  });
}

/**
 * Compute for one item, as two separate figure sets. The response carries no
 * combined figure and none must be derived from it.
 */
export function useTaskCompute(id: string) {
  return useQuery({
    queryKey: taskKeys.compute(id),
    queryFn: () => tasksApi.getCompute(id),
    enabled: !!id,
  });
}
